/* Standish Milling smart search. Plain JS, no libraries, loaded with `defer` on every page.

   How it works
   - On first use (focus, tap or the search page) the whole catalog is read once as a small JSON index from
     /collections/all?view=smc-index (about 2,700 products) and cached in localStorage for 6 hours.
   - Until the index is ready, the dropdown uses Shopify's /search/suggest.json so the first keystroke is never empty.
   - With the index the dropdown answers in a few milliseconds, on every keystroke, with no server round trip:
     every word must match (so "deer corn" is not "corn" or "deer"), prefix matches count while typing, whole phrases
     rank first, sizes are understood (50 lb = 50lb = 50#), typos are repaired ("sunflour", "birdseed" <-> "bird seed"),
     and a synonym list from the theme settings widens the query ("rock salt" also finds ice melt).
   - The dropdown groups results by department and brand, shows size and price, remembers recent searches, offers
     popular searches and departments on an empty box, and on a miss shows suggestions and the store phone number.
   - On /search the same engine can render the results with department, brand, stock and price filters. */
(function () {
  'use strict';

  var INDEX_KEY = 'smc-idx-v2';  // v2 adds item codes (s)
  var INDEX_TTL = 6 * 60 * 60 * 1000;
  var RECENT_KEY = 'smc-recent-v1';
  var SHEET_QUERY = '(max-width: 767.98px)';
  var DEBOUNCE_FALLBACK_MS = 200; // only the network fallback is debounced; index search runs every keystroke

  function $(s, r) { return (r || document).querySelector(s); }
  function $$(s, r) { return Array.prototype.slice.call((r || document).querySelectorAll(s)); }
  function el(tag, cls, text) { var n = document.createElement(tag); if (cls) n.className = cls; if (text != null) n.textContent = text; return n; }
  function store(get, key, val) {
    try { if (get) return window.localStorage.getItem(key); window.localStorage.setItem(key, val); } catch (e) { /* private mode */ }
    return null;
  }

  /* ================================================================= engine */
  var UNIT = { lbs: 'lb', lb: 'lb', pound: 'lb', pounds: 'lb', oz: 'oz', ounce: 'oz', ounces: 'oz', gal: 'gal', gallon: 'gal', gallons: 'gal', qt: 'qt', quart: 'qt', quarts: 'qt', kg: 'kg', ft: 'ft', foot: 'ft', feet: 'ft' };
  var STOP = { the: 1, and: 1, of: 1, for: 1, a: 1, an: 1, with: 1, in: 1, to: 1, by: 1 };

  function norm(s) {
    s = String(s || '').toLowerCase()
      .replace(/(\d)\s*#/g, '$1 lb ')             // 50# -> 50 lb
      .replace(/&/g, ' and ')
      .replace(/['’]/g, '')
      .replace(/(\d)([a-z])/g, '$1 $2').replace(/([a-z])(\d)/g, '$1 $2')
      .replace(/[^a-z0-9]+/g, ' ').trim();
    return s;
  }
  function tokens(s) {
    return norm(s).split(' ').filter(Boolean).map(function (t) { return UNIT[t] || t; });
  }

  function lowerBound(arr, x) { var lo = 0, hi = arr.length; while (lo < hi) { var m = (lo + hi) >> 1; if (arr[m] < x) lo = m + 1; else hi = m; } return lo; }

  // Damerau-Levenshtein (optimal string alignment), early exit above max.
  function dist(a, b, max) {
    var la = a.length, lb = b.length;
    if (Math.abs(la - lb) > max) return max + 1;
    var prev2 = null, prev = [], cur = [], i, j;
    for (j = 0; j <= lb; j++) prev[j] = j;
    for (i = 1; i <= la; i++) {
      cur = [i]; var rowMin = i;
      for (j = 1; j <= lb; j++) {
        var c = a.charCodeAt(i - 1) === b.charCodeAt(j - 1) ? 0 : 1;
        var v = Math.min(prev[j] + 1, cur[j - 1] + 1, prev[j - 1] + c);
        if (i > 1 && j > 1 && a.charCodeAt(i - 1) === b.charCodeAt(j - 2) && a.charCodeAt(i - 2) === b.charCodeAt(j - 1)) v = Math.min(v, prev2[j - 2] + 1);
        cur[j] = v; if (v < rowMin) rowMin = v;
      }
      if (rowMin > max) return max + 1;
      prev2 = prev; prev = cur;
    }
    return prev[lb];
  }

  function Engine(items, synonymText) {
    var self = this;
    this.items = items;
    this.docs = items.map(function (it) {
      var tw = tokens(it.t), vw = tokens(it.v), yw = tokens(it.y), gw = tokens(it.g);
      var sk = String(it.s || '').toLowerCase().split(/\s+/).filter(Boolean);
      return { it: it, tn: ' ' + tw.join(' ') + ' ', tw: tw, vw: vw, yw: yw, gw: gw, sk: sk, len: it.t.length };
    });
    var freq = {};
    this.docs.forEach(function (d) {
      [d.tw, d.vw, d.yw, d.gw].forEach(function (arr) { arr.forEach(function (w) { if (w.length > 1 && !/^\d+$/.test(w)) freq[w] = (freq[w] || 0) + 1; }); });
    });
    this.freq = freq;
    this.vocab = Object.keys(freq).sort();
    this.groups = parseSynonyms(synonymText);
    this.brands = {};
    items.forEach(function (it) { if (it.v) self.brands[it.v] = (self.brands[it.v] || 0) + 1; });
  }

  function parseSynonyms(text) {
    var groups = [];
    String(text || '').split(/\n/).forEach(function (line) {
      var parts = line.split(/[=,]/).map(function (p) { return tokens(p).join(' '); }).filter(Boolean);
      if (parts.length > 1) groups.push(parts);
    });
    return groups;
  }

  Engine.prototype.hasPrefix = function (w) {
    var i = lowerBound(this.vocab, w);
    return i < this.vocab.length && this.vocab[i].indexOf(w) === 0;
  };
  Engine.prototype.isWord = function (w) { return this.freq[w] != null; };

  // Nearest vocabulary word within the typo budget; ties go to the more common word.
  Engine.prototype.nearest = function (w) {
    var max = w.length <= 3 ? 0 : w.length <= 5 ? 1 : 2, best = null, bd = 99, bf = 0, v = this.vocab, i, d;
    if (!max) return null;
    for (i = 0; i < v.length; i++) {
      if (Math.abs(v[i].length - w.length) > max) continue;
      if (v[i].charAt(0) !== w.charAt(0) && max < 2) continue;
      d = dist(w, v[i], max);
      if (d <= max && (d < bd || (d === bd && this.freq[v[i]] > bf))) { best = v[i]; bd = d; bf = this.freq[v[i]]; }
    }
    return best;
  };

  // Repair one token: known word or word-prefix stays; "birdseed" splits; a typo goes to the nearest word.
  Engine.prototype.repair = function (w, isLast) {
    if (/^\d+$/.test(w) || this.isWord(w) || (isLast && this.hasPrefix(w)) || (w.length < 4 && this.hasPrefix(w))) return { t: [w], fixed: false };
    var i, a, b, best = null, bs = 0;
    for (i = 3; i <= w.length - 3; i++) {
      a = w.slice(0, i); b = w.slice(i);
      if (this.isWord(a) && this.isWord(b)) { var s = Math.min(this.freq[a], this.freq[b]); if (s > bs) { bs = s; best = [a, b]; } }
    }
    if (best) return { t: best, fixed: true };
    var n = this.nearest(w);
    if (n) return { t: [n], fixed: true };
    return { t: [w], fixed: false };
  };

  // The query becomes a few alternative token lists: as typed (repaired), joined words, synonyms.
  Engine.prototype.expand = function (q) {
    var self = this, base = tokens(q).filter(function (t) { return !STOP[t]; }), fixed = false, rep = [];
    base.forEach(function (w, idx) { var r = self.repair(w, idx === base.length - 1); if (r.fixed) fixed = true; rep = rep.concat(r.t); });
    var alts = [{ t: rep, w: 1.1 }], seen = {}; seen[rep.join(' ')] = 1;
    function add(t, w) { var k = t.join(' '); if (k && !seen[k]) { seen[k] = 1; alts.push({ t: t, w: w }); } }
    // joined pairs ("bird seed" -> "birdseed")
    for (var i = 0; i + 1 < rep.length; i++) {
      var j = rep[i] + rep[i + 1];
      if (self.isWord(j)) add(rep.slice(0, i).concat([j], rep.slice(i + 2)), 0.95);
    }
    // synonyms: a group member that appears in the query as a phrase swaps for every other member
    var joined = ' ' + rep.join(' ') + ' ';
    this.groups.forEach(function (g) {
      g.forEach(function (m) {
        var at = joined.indexOf(' ' + m + ' ');
        if (at > -1) {
          var before = joined.slice(0, at).trim(), after = joined.slice(at + m.length + 2).trim();
          g.forEach(function (o) {
            if (o === m) return;
            add((before + ' ' + o + ' ' + after).trim().split(/\s+/), 0.9);
          });
        }
      });
    });
    return { alts: alts, fixed: fixed, shown: rep.join(' ') };
  };

  function scoreDoc(eng, d, alt, relaxed) {
    var s = 0, i, t, tw = d.tw, hit, k, partial = 0, textHit = false;
    for (i = 0; i < alt.t.length; i++) {
      t = alt.t[i]; hit = 0;
      var num = /^\d+$/.test(t), last = i === alt.t.length - 1;
      if (num && relaxed) { partial++; continue; }
      for (k = 0; k < tw.length; k++) {
        if (tw[k] === t) { hit = Math.max(hit, 10); break; }
        if (!num && t.length > 1 && tw[k].indexOf(t) === 0) hit = Math.max(hit, 6);
        else if (num && last && t.length > 1 && tw[k].indexOf(t) === 0) hit = Math.max(hit, 4);
      }
      if (hit < 10) {
        var f = function (arr, exact, pre) {
          for (var x = 0; x < arr.length; x++) {
            if (arr[x] === t) { hit = Math.max(hit, exact); return; }
            if (!num && t.length > 1 && arr[x].indexOf(t) === 0) hit = Math.max(hit, pre);
          }
        };
        f(d.yw, 5, 3); f(d.vw, 5, 3); f(d.gw, 3, 2);
      }
      if (!hit) { partial++; continue; }
      if (!num) textHit = true;
      s += hit;
    }
    if (partial) {
      if (!relaxed) return -1;                  // normal pass: every word must match
      if (partial >= alt.t.length) return -1;
      if (!textHit) return -1;                   // a bare number ("10") is not a match on its own
      s = s * 0.45 - 6;                          // relaxed pass: some words missing, ranked below any full match
    } else if (relaxed) return -1;
    var phrase = ' ' + alt.t.join(' ') + ' ';
    var seenNum = {}, dupNum = false;
    alt.t.forEach(function (t) { if (/^\d+$/.test(t)) { if (seenNum[t]) dupNum = true; seenNum[t] = 1; } });
    if (dupNum && !relaxed) {                                        // "10-10-10" must appear as a run, not three loose 10s
      var run = alt.t.filter(function (t) { return /^\d+$/.test(t); }).join(' ');
      if (d.tn.indexOf(' ' + run + ' ') < 0) return -1;
    }
    var inType = alt.t.every(function (t) { return d.yw.some(function (w) { return w === t || (!/^\d+$/.test(t) && t.length > 2 && w.indexOf(t) === 0); }); });
    var syn = alt.w < 1;                                  // a synonym phrase earns smaller bonuses than the words typed
    if (inType && alt.t.length) s += syn ? 6 : 10;                  // the product's department is what was asked for
    if (alt.t.length > 1 && d.tn.indexOf(phrase) > -1) s += syn ? 8 : 14;
    else if (alt.t.length > 1 && d.tn.indexOf(' ' + alt.t.join(' ')) > -1) s += 8;
    if (d.tw.length && d.tw[0].indexOf(alt.t[0]) === 0) s += 4;
    s += Math.max(0, 3 * (1 - d.len / 110));
    if (/\b(alternative to|substitute for|instead of)\b/i.test(d.it.t)) s *= 0.5;   // "Alternative to Rock Salt" is not rock salt
    return s * alt.w;
  }

  Engine.prototype.run = function (ex, relaxed) {
    var out = [], i, a, sc, best;
    for (i = 0; i < this.docs.length; i++) {
      best = -1;
      for (a = 0; a < ex.alts.length; a++) { sc = scoreDoc(this, this.docs[i], ex.alts[a], relaxed); if (sc > best) best = sc; }
      if (best >= 0) out.push({ d: this.docs[i], s: best + (this.docs[i].it.a ? 0 : -1000) });
    }
    out.sort(function (x, y) { return y.s - x.s; });
    return out;
  };

  // A typo that is itself a word ("slat" for "salt"): swap each word for a close, much more common one.
  Engine.prototype.forceFix = function (q) {
    var self = this, base = tokens(q).filter(function (t) { return !STOP[t]; }), changed = false;
    var fixedTokens = base.map(function (w) {
      if (/^\d+$/.test(w) || w.length < 4) return w;
      var best = null, bf = self.freq[w] || 0, v = self.vocab, i;
      for (i = 0; i < v.length; i++) {
        if (Math.abs(v[i].length - w.length) > 1 || v[i] === w) continue;
        if (self.freq[v[i]] > bf * 3 && dist(w, v[i], 1) <= 1 && self.freq[v[i]] > (best ? self.freq[best] : 0)) best = v[i];
      }
      if (best) { changed = true; return best; }
      return w;
    });
    return changed ? { alts: [{ t: fixedTokens, w: 1 }], fixed: true, shown: fixedTokens.join(' ') } : null;
  };

  // An item code typed as the whole query ("123", "281892-5"): the exact code first, then codes that start
  // with it (3+ characters), ahead of any word match. Counter staff and dealers search by SMC's codes.
  Engine.prototype.codes = function (q) {
    var c = String(q || '').trim().toLowerCase();
    if (!/^[a-z0-9][a-z0-9-]{1,30}$/.test(c) || !/\d/.test(c)) return [];
    var exact = [], prefix = [];
    this.docs.forEach(function (d) {
      if (d.sk.indexOf(c) > -1) exact.push({ d: d, s: 10000 + (d.it.a ? 1 : 0), code: c });
      else if (c.length >= 3 && d.sk.some(function (k) { return k.indexOf(c) === 0; })) prefix.push({ d: d, s: 5000 + (d.it.a ? 1 : 0), code: c });
    });
    var by = function (x, y) { return y.s - x.s || x.d.len - y.d.len; };
    return exact.sort(by).concat(prefix.sort(by).slice(0, 20));
  };

  Engine.prototype.search = function (q) {
    var hits = this.codes(q);
    if (hits.length) {
      var seen = {}; hits.forEach(function (r) { seen[r.d.it.h] = 1; });
      var rest = this.searchWords(q).list.filter(function (r) { return !seen[r.d.it.h]; });
      return { list: hits.concat(rest), fixed: false, shown: String(q).trim(), ex: this.expand(q), byCode: true };
    }
    return this.searchWords(q);
  };

  Engine.prototype.searchWords = function (q) {
    var ex = this.expand(q), out;
    if (!ex.alts[0].t.length) return { list: [], fixed: false, shown: '', ex: ex };
    out = this.run(ex, false);
    if (!out.length) {
      var fx = this.forceFix(q);
      if (fx) { out = this.run(fx, false); if (out.length) ex = fx; }
    }
    if (out.length < 4 && ex.alts[0].t.length > 1) {   // thin result: add partial matches after the full ones
      var have = {}; out.forEach(function (r) { have[r.d.it.h] = 1; });
      this.run(ex, true).forEach(function (r) { if (!have[r.d.it.h]) out.push(r); });
    }
    return { list: out, fixed: ex.fixed, shown: ex.shown, ex: ex };
  };

  // Department and brand tallies over the best matches.
  function tally(list, field, limit) {
    var c = {}, order = [];
    list.slice(0, 150).forEach(function (r) { var k = r.d.it[field]; if (!k) return; if (!c[k]) { c[k] = 0; order.push(k); } c[k]++; });
    order.sort(function (a, b) { return c[b] - c[a]; });
    return order.slice(0, limit).map(function (k) { return { name: k, n: c[k] }; });
  }

  var SIZE_RE = /(\d+(?:\.\d+)?)\s*(lbs?|#|oz|gal(?:lons?)?|qt|quarts?|kg|ft|in|inch|"|l|ml|ct|pack|pk)\b\.?/i;
  function brandLabel(v) { return String(v || '').replace(/\s*\(Standish Milling Company\)\s*/i, '').trim(); }
  function sizeOf(title) {
    var m = String(title).match(SIZE_RE);
    if (!m) return '';
    var u = m[2].toLowerCase();
    u = u === '#' || u === 'lbs' ? 'lb' : /^gal/.test(u) ? 'gal' : /^quart/.test(u) ? 'qt' : u === 'pk' ? 'pack' : u;
    return m[1] + ' ' + u;
  }

  /* ====================================================== index loading */
  var indexPromise = null, engine = null, cfgGlobal = null;

  function loadIndex(cfg) {
    if (indexPromise) return indexPromise;
    indexPromise = new Promise(function (resolve) {
      var cached = null;
      try { cached = JSON.parse(store(true, INDEX_KEY) || 'null'); } catch (e) { cached = null; }
      var fresh = cached && cached.items && Date.now() - cached.t < INDEX_TTL;
      if (cached && cached.items && cached.items.length) {
        engine = new Engine(cached.items, cfg.synonyms);
        resolve(engine);
        if (fresh) return;
      }
      fetchAll().then(function (items) {
        if (!items.length) return;
        store(false, INDEX_KEY, JSON.stringify({ t: Date.now(), items: items }));
        if (!engine) { engine = new Engine(items, cfg.synonyms); resolve(engine); }
      }).catch(function (e) { if (window.console) console.error('[smc-search] index failed: ' + (e && e.stack || e)); if (!engine) resolve(null); });
    });
    return indexPromise;
  }

  function fetchAll() {
    function page(n) {
      return fetch('/collections/all?view=smc-index&page=' + n)
        .then(function (r) { if (!r.ok) throw new Error('HTTP ' + r.status); return r.json(); });
    }
    return page(1).then(function (first) {
      var items = first.items || [], pages = first.pages || 1, next = 2, running = 0;
      return new Promise(function (resolve, reject) {
        if (pages <= 1) return resolve(items);
        var done = 0, failed = false;
        function pump() {
          while (running < 4 && next <= pages) {
            (function (n) {
              running++;
              page(n).then(function (j) { items = items.concat(j.items || []); })
                .catch(function () { failed = true; })
                .then(function () { running--; done++; if (done === pages - 1) failed ? reject(new Error('partial')) : resolve(items); else pump(); });
            })(next++);
          }
        }
        pump();
      });
    });
  }

  /* ========================================================= recent searches */
  function getRecent() { try { return JSON.parse(store(true, RECENT_KEY) || '[]'); } catch (e) { return []; } }
  function saveRecent(q) {
    q = String(q || '').trim(); if (q.length < 2) return;
    var r = getRecent().filter(function (x) { return x.toLowerCase() !== q.toLowerCase(); });
    r.unshift(q); store(false, RECENT_KEY, JSON.stringify(r.slice(0, 6)));
  }
  function dropRecent(q) { store(false, RECENT_KEY, JSON.stringify(getRecent().filter(function (x) { return x !== q; }))); }

  /* ================================================================ widget */
  function initSearch(root) {
    var input = $('[data-smc-input]', root), panel = $('[data-smc-suggest]', root), closeBtn = $('[data-smc-search-close]', root);
    var form = input && input.form;
    if (!input || !panel || !form) return;
    var cfg = {}; try { cfg = JSON.parse(($('[data-smc-search-config]') || {}).textContent || '{}'); } catch (e) { cfg = {}; }
    cfgGlobal = cfg;
    var limit = parseInt(root.getAttribute('data-limit'), 10) || 6;
    var currency = root.getAttribute('data-currency') || 'USD';
    var fmt; try { fmt = new Intl.NumberFormat(undefined, { style: 'currency', currency: currency }); } catch (e) { fmt = { format: function (n) { return '$' + n.toFixed(2); } }; }
    var sheetMQ = window.matchMedia ? window.matchMedia(SHEET_QUERY) : { matches: false };
    var timer = null, controller = null, cache = {}, lastQuery = '', items = [], active = -1, seq = 0;

    function money(c) { return fmt.format((c || 0) / 100); }
    function searchUrl(q, extra) { return form.getAttribute('action') + '?q=' + encodeURIComponent(q) + '&options%5Bprefix%5D=last' + (extra || ''); }
    function isOpen() { return !panel.hidden; }

    function setActive(i) {
      if (active > -1 && items[active]) items[active].setAttribute('aria-selected', 'false');
      active = i;
      if (i > -1 && items[i]) { items[i].setAttribute('aria-selected', 'true'); input.setAttribute('aria-activedescendant', items[i].id); items[i].scrollIntoView({ block: 'nearest' }); }
      else input.removeAttribute('aria-activedescendant');
    }
    function open() { panel.hidden = false; input.setAttribute('aria-expanded', 'true'); }
    function close() { panel.hidden = true; input.setAttribute('aria-expanded', 'false'); input.removeAttribute('aria-activedescendant'); active = -1; items = []; }
    function fitSheet() { var vv = window.visualViewport; if (vv) root.style.setProperty('--smc-sheet-h', vv.height + 'px'); }
    function openSheet() {
      if (!sheetMQ.matches || root.classList.contains('is-sheet')) return;
      root.classList.add('is-sheet'); document.body.classList.add('is-search-open'); fitSheet();
      if (window.visualViewport) window.visualViewport.addEventListener('resize', fitSheet);
    }
    function closeSheet() {
      root.classList.remove('is-sheet'); document.body.classList.remove('is-search-open'); root.style.removeProperty('--smc-sheet-h');
      if (window.visualViewport) window.visualViewport.removeEventListener('resize', fitSheet);
    }

    function option(href, cls) {
      var a = el('a', cls); a.href = href; a.id = 'smc-opt-' + (++seq);
      a.setAttribute('role', 'option'); a.setAttribute('aria-selected', 'false');
      return a;
    }
    function group(title, extraCls) {
      var g = el('div', 'smc-suggest__group' + (extraCls ? ' ' + extraCls : '')); g.setAttribute('role', 'group');
      var h = el('div', 'smc-suggest__h', title); h.id = 'smc-grp-' + (++seq); g.setAttribute('aria-labelledby', h.id); g.appendChild(h);
      return g;
    }
    function chips(list, hrefFn, cls) {
      var wrap = el('div', 'smc-suggest__chips');
      list.forEach(function (x) {
        var a = el('a', 'smc-chip' + (cls ? ' ' + cls : ''), x.label); a.href = hrefFn(x);
        wrap.appendChild(a);
      });
      return wrap;
    }
    function highlight(node, text, q) {
      var toks = tokens(q).filter(function (t) { return t.length > 1 && !/^\d+$/.test(t); });
      if (!toks.length) { node.textContent = text; return; }
      var re = new RegExp('(' + toks.map(function (t) { return t.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'); }).join('|') + ')', 'ig');
      text.split(re).forEach(function (part, i) { if (i % 2) node.appendChild(el('mark', 'smc-hl', part)); else if (part) node.appendChild(document.createTextNode(part)); });
    }
    function finish() { items = $$('[role="option"]', panel); active = -1; input.removeAttribute('aria-activedescendant'); open(); }

    /* ---- empty box: recent, popular, departments ---- */
    function renderEmpty() {
      panel.textContent = '';
      var rec = getRecent();
      if (rec.length) {
        var g = group('Recent searches');
        rec.forEach(function (q) {
          var row = el('div', 'smc-suggest__recent');
          var a = option(searchUrl(q), 'smc-suggest__item smc-suggest__item--slim');
          a.appendChild(el('span', 'smc-suggest__title', q)); row.appendChild(a);
          var x = el('button', 'smc-suggest__x', '×'); x.type = 'button'; x.setAttribute('aria-label', 'Remove ' + q + ' from recent searches');
          x.addEventListener('mousedown', function (e) { e.preventDefault(); });
          x.addEventListener('click', function () { dropRecent(q); renderEmpty(); });
          row.appendChild(x); g.appendChild(row);
        });
        panel.appendChild(g);
      }
      if (cfg.popular && cfg.popular.length) {
        var gp = group('Popular searches', 'smc-suggest__group--chips');
        gp.appendChild(chips(cfg.popular.map(function (l) { return { label: l }; }), function (x) { return searchUrl(x.label); }));
        panel.appendChild(gp);
      }
      if (cfg.departments && cfg.departments.length) {
        var gd = group('Shop by department', 'smc-suggest__group--chips');
        gd.appendChild(chips(cfg.departments, function (x) { return x.url; }, 'smc-chip--dept'));
        panel.appendChild(gd);
      }
      panel.appendChild(helpRow(false));
      finish();
    }

    function helpRow(miss) {
      var h = el('div', 'smc-suggest__help');
      h.appendChild(document.createTextNode(miss ? 'Cannot find it? ' : 'Need a hand? '));
      if (cfg.phone) { var a = el('a', null, 'Call ' + cfg.phone); a.href = 'tel:' + String(cfg.phone).replace(/[^\d+]/g, ''); h.appendChild(a); }
      if (cfg.contact_url) { h.appendChild(document.createTextNode(' or ')); var c = el('a', null, 'send us a message'); c.href = cfg.contact_url; h.appendChild(c); }
      h.appendChild(document.createTextNode('.'));
      return h;
    }

    /* ---- results from the local index ---- */
    function renderIndex(q, res) {
      panel.textContent = '';
      var list = res.list, shownQ = res.fixed ? res.shown : q;
      if (!list.length) return renderMiss(q);
      if (res.fixed) {
        var note = el('div', 'smc-suggest__note');
        note.appendChild(document.createTextNode('Showing results for '));
        note.appendChild(el('strong', null, res.shown));
        panel.appendChild(note);
      }
      var gp = group('Products');
      list.slice(0, limit + 2).forEach(function (r) {
        var it = r.d.it, a = option('/products/' + it.h + '?_psq=' + encodeURIComponent(q), 'smc-suggest__item');
        a.setAttribute('data-q', q);
        if (it.i) { var im = el('img', 'smc-suggest__thumb'); im.src = '/cdn/shop/' + it.i + (it.i.indexOf('?') > -1 ? '&' : '?') + 'width=104'; im.width = 52; im.height = 52; im.alt = ''; im.loading = 'lazy'; a.appendChild(im); }
        else a.appendChild(el('span', 'smc-suggest__thumb smc-suggest__thumb--empty'));
        var t = el('span', 'smc-suggest__text'), title = el('span', 'smc-suggest__title');
        highlight(title, it.t, shownQ); t.appendChild(title);
        var codeHit = r.code ? r.d.sk.filter(function (k) { return k.indexOf(r.code) === 0; })[0] : '';
        var sub = [codeHit ? 'Item ' + codeHit.toUpperCase() : '', sizeOf(it.t), it.v && it.v !== 'Standish Milling Company' ? brandLabel(it.v) : '', it.n > 1 ? it.n + ' options' : ''].filter(Boolean).join(' · ');
        if (sub) t.appendChild(el('span', 'smc-suggest__sub', sub));
        if (!it.a) t.appendChild(el('span', 'smc-suggest__soldout', 'Sold out'));
        a.appendChild(t);
        var price = el('span', 'smc-suggest__price', (it.n > 1 ? 'From ' : '') + money(it.p));
        if (it.c > it.p) price.appendChild(el('s', null, money(it.c)));
        a.appendChild(price); gp.appendChild(a);
      });
      panel.appendChild(gp);
      var types = tally(list, 'y', 3), brands = tally(list, 'v', 2).filter(function (b) { return b.name !== 'Standish Milling Company' && b.n >= 3; });
      if (types.length > 1 || brands.length) {
        var gd = group('Narrow it down', 'smc-suggest__group--chips'), wrap = el('div', 'smc-suggest__chips');
        types.forEach(function (x) {
          var a = option(searchUrl(shownQ, '&filter.p.product_type=' + encodeURIComponent(x.name)), 'smc-chip smc-chip--dept');
          a.textContent = x.name + ' (' + x.n + ')'; wrap.appendChild(a);
        });
        brands.forEach(function (x) {
          var a = option(searchUrl(shownQ, '&filter.p.vendor=' + encodeURIComponent(x.name)), 'smc-chip');
          a.textContent = brandLabel(x.name) + ' (' + x.n + ')'; wrap.appendChild(a);
        });
        gd.appendChild(wrap); panel.appendChild(gd);
      }
      var navMatch = (cfg.departments || []).filter(function (d) { var n = tokens(d.label).join(' '); var qq = tokens(q).join(' '); return qq.length > 2 && (n.indexOf(qq) === 0 || n.indexOf(' ' + qq) > -1); }).slice(0, 2);
      if (navMatch.length) {
        var gn = group('Departments');
        navMatch.forEach(function (d) { var a = option(d.url, 'smc-suggest__item smc-suggest__item--slim'); a.appendChild(el('span', 'smc-suggest__title', 'Shop ' + d.label)); gn.appendChild(a); });
        panel.appendChild(gn);
      }
      var all = option(searchUrl(shownQ), 'smc-suggest__all');
      all.appendChild(el('span', null, 'See all ' + list.length + (list.length === 1 ? ' result' : ' results') + ' for “' + shownQ + '”'));
      all.insertAdjacentHTML('beforeend', '<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M5 12h14M13 6l6 6-6 6"/></svg>');
      panel.appendChild(all);
      finish();
      pushEvent(q, list.length);
    }

    function renderMiss(q) {
      panel.textContent = '';
      var m = el('div', 'smc-suggest__msg');
      m.appendChild(document.createTextNode('No matches for “' + q + '”. Try fewer words or a different spelling.'));
      panel.appendChild(m);
      if (cfg.popular && cfg.popular.length) {
        var gp = group('Try one of these', 'smc-suggest__group--chips');
        gp.appendChild(chips(cfg.popular.map(function (l) { return { label: l }; }), function (x) { return searchUrl(x.label); }));
        panel.appendChild(gp);
      }
      if (cfg.departments && cfg.departments.length) {
        var gd = group('Or browse a department', 'smc-suggest__group--chips');
        gd.appendChild(chips(cfg.departments, function (x) { return x.url; }, 'smc-chip--dept'));
        panel.appendChild(gd);
      }
      panel.appendChild(helpRow(true));
      finish();
      pushEvent(q, 0);
    }

    var lastPush = '';
    function pushEvent(q, n) {
      if (lastPush === q) return; lastPush = q;
      window.dataLayer = window.dataLayer || [];
      clearTimeout(pushEvent.t);
      pushEvent.t = setTimeout(function () { window.dataLayer.push({ event: 'smc_search', search_term: q, search_results: n }); }, 900);
    }

    /* ---- network fallback (until the index is ready) ---- */
    function renderSuggest(q, data) {
      var res = (data && data.resources && data.resources.results) || {};
      var products = (res.products || []).slice(0, limit), collections = (res.collections || []).slice(0, 3);
      panel.textContent = '';
      if (!products.length && !collections.length) return renderMiss(q);
      if (products.length) {
        var gp = group('Products');
        products.forEach(function (p) {
          var a = option(p.url, 'smc-suggest__item'), img = (p.featured_image && p.featured_image.url) || p.image;
          if (img) { var im = el('img', 'smc-suggest__thumb'); im.src = img + (img.indexOf('?') > -1 ? '&' : '?') + 'width=104'; im.width = 52; im.height = 52; im.alt = ''; a.appendChild(im); }
          else a.appendChild(el('span', 'smc-suggest__thumb smc-suggest__thumb--empty'));
          var t = el('span', 'smc-suggest__text'); t.appendChild(el('span', 'smc-suggest__title', p.title));
          var sub = [sizeOf(p.title), p.vendor && p.vendor !== 'Standish Milling Company' ? p.vendor : ''].filter(Boolean).join(' · ');
          if (sub) t.appendChild(el('span', 'smc-suggest__sub', sub));
          if (p.available === false) t.appendChild(el('span', 'smc-suggest__soldout', 'Sold out'));
          a.appendChild(t);
          var pmin = parseFloat(p.price_min || p.price), pmax = parseFloat(p.price_max || p.price);
          a.appendChild(el('span', 'smc-suggest__price', (pmax > pmin ? 'From ' : '') + money(pmin * 100)));
          gp.appendChild(a);
        });
        panel.appendChild(gp);
      }
      if (collections.length) {
        var gc = group('Departments');
        collections.forEach(function (c) { var a = option(c.url, 'smc-suggest__item smc-suggest__item--slim'); a.appendChild(el('span', 'smc-suggest__title', c.title)); gc.appendChild(a); });
        panel.appendChild(gc);
      }
      var all = option(searchUrl(q), 'smc-suggest__all'); all.appendChild(el('span', null, 'See all results for “' + q + '”')); panel.appendChild(all);
      finish();
    }
    function fetchSuggest(q) {
      if (cache[q]) return renderSuggest(q, cache[q]);
      if (controller && controller.abort) controller.abort();
      controller = window.AbortController ? new AbortController() : null;
      var url = '/search/suggest.json?q=' + encodeURIComponent(q) + '&resources%5Btype%5D=product,collection&resources%5Blimit%5D=' + Math.min(Math.max(limit, 3), 10) + '&resources%5Boptions%5D%5Bunavailable_products%5D=last';
      fetch(url, { headers: { Accept: 'application/json' }, signal: controller ? controller.signal : undefined })
        .then(function (r) { if (!r.ok) throw new Error('HTTP ' + r.status); return r.json(); })
        .then(function (d) { cache[q] = d; if (q === lastQuery && !engine) renderSuggest(q, d); })
        .catch(function (err) { if (err && err.name === 'AbortError') return; if (q === lastQuery) renderMiss(q); });
    }

    function run() {
      var q = input.value.trim();
      lastQuery = q;
      if (q.length < 2) { if (controller && controller.abort) controller.abort(); renderEmpty(); return; }
      if (engine) { renderIndex(q, engine.search(q)); return; }
      clearTimeout(timer);
      timer = setTimeout(function () { if (q === lastQuery && !engine) fetchSuggest(q); }, DEBOUNCE_FALLBACK_MS);
    }

    function warm() { loadIndex(cfg).then(function () { if (document.activeElement === input || root.classList.contains('is-sheet')) run(); }); }

    input.addEventListener('input', run);
    input.addEventListener('focus', function () { openSheet(); warm(); run(); });
    input.addEventListener('click', function () { openSheet(); if (panel.hidden) run(); });
    root.addEventListener('pointerenter', function () { loadIndex(cfg); }, { once: true });

    input.addEventListener('keydown', function (e) {
      if (e.isComposing) return;
      var live = isOpen() && items.length;
      if (e.key === 'ArrowDown') {
        if (!live) { run(); return; }
        e.preventDefault(); setActive(active + 1 >= items.length ? 0 : active + 1);
      } else if (e.key === 'ArrowUp') {
        if (!live) return;
        e.preventDefault(); setActive(active <= 0 ? items.length - 1 : active - 1);
      } else if (e.key === 'Enter') {
        if (live && active > -1 && items[active]) { e.preventDefault(); saveRecent(input.value); window.location.href = items[active].href; }
        else saveRecent(input.value);
      } else if (e.key === 'Escape') {
        if (isOpen()) { e.preventDefault(); close(); }
        else if (root.classList.contains('is-sheet')) { e.preventDefault(); closeSheet(); input.blur(); }
        else if (input.value) input.value = '';
      }
    });
    panel.addEventListener('mousemove', function (e) { var o = e.target.closest && e.target.closest('[role="option"]'); if (o) { var i = items.indexOf(o); if (i > -1 && i !== active) setActive(i); } });
    panel.addEventListener('click', function (e) { var a = e.target.closest && e.target.closest('a'); if (a && (a.getAttribute('data-q') || a.classList.contains('smc-suggest__all') || a.classList.contains('smc-chip'))) saveRecent(a.getAttribute('data-q') || input.value); });
    form.addEventListener('submit', function () { clearTimeout(timer); saveRecent(input.value); });
    if (closeBtn) closeBtn.addEventListener('click', function () { close(); closeSheet(); input.blur(); });
    document.addEventListener('click', function (e) { if (!root.contains(e.target)) close(); });
    if (sheetMQ.addEventListener) sheetMQ.addEventListener('change', function () { if (!sheetMQ.matches) closeSheet(); });
    document.addEventListener('keydown', function (e) {
      if (e.key !== '/' || e.ctrlKey || e.metaKey || e.altKey) return;
      var t = e.target, tag = t && t.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || (t && t.isContentEditable)) return;
      e.preventDefault(); input.focus(); input.select();
    });
    window.addEventListener('pageshow', function () { closeSheet(); });
    window.smcSearch = { ready: function () { return loadIndex(cfg); }, search: function (q) { return engine ? engine.search(q) : null; }, engine: function () { return engine; }, sizeOf: sizeOf };
  }

  /* ============================================== search results page (smart) */
  function initResultsPage(box) {
    var q = box.getAttribute('data-q') || '';
    var native = $('[data-smc-native]');
    var cfg = cfgGlobal || {};
    if (!q) return;
    var state = { type: '', brand: '', stock: false, sort: 'best', shown: 24 };
    var params = new URLSearchParams(location.search);
    if (params.get('filter.p.product_type')) state.type = params.get('filter.p.product_type');
    if (params.get('filter.p.vendor')) state.brand = params.get('filter.p.vendor');
    var fmt; try { fmt = new Intl.NumberFormat(undefined, { style: 'currency', currency: box.getAttribute('data-currency') || 'USD' }); } catch (e) { fmt = { format: function (n) { return '$' + n.toFixed(2); } }; }
    var head = $('[data-smc-smart-head]', box), bar = $('[data-smc-smart-bar]', box), grid = $('[data-smc-smart-grid]', box), more = $('[data-smc-smart-more]', box), toggle = $('[data-smc-smart-toggle]');
    var res = null;

    function apply() {
      var list = res.list.filter(function (r) {
        var it = r.d.it;
        return (!state.type || it.y === state.type) && (!state.brand || it.v === state.brand) && (!state.stock || it.a);
      });
      if (state.sort === 'low') list = list.slice().sort(function (a, b) { return a.d.it.p - b.d.it.p; });
      else if (state.sort === 'high') list = list.slice().sort(function (a, b) { return b.d.it.p - a.d.it.p; });
      else if (state.sort === 'az') list = list.slice().sort(function (a, b) { return a.d.it.t.localeCompare(b.d.it.t); });
      return list;
    }
    function chip(label, on, fn) { var b = el('button', 'smc-chip' + (on ? ' is-on' : ''), label); b.type = 'button'; b.setAttribute('aria-pressed', on ? 'true' : 'false'); b.addEventListener('click', fn); return b; }
    function renderBar() {
      bar.textContent = '';
      var types = tally(res.list, 'y', 8), brands = tally(res.list, 'v', 8).filter(function (b) { return b.name !== 'Standish Milling Company'; });
      var row1 = el('div', 'smc-smart__row'); row1.appendChild(el('span', 'smc-smart__label', 'Department'));
      row1.appendChild(chip('All', !state.type, function () { state.type = ''; state.shown = 24; render(); }));
      types.forEach(function (t) { row1.appendChild(chip(t.name + ' (' + t.n + ')', state.type === t.name, function () { state.type = state.type === t.name ? '' : t.name; state.shown = 24; render(); })); });
      bar.appendChild(row1);
      if (brands.length > 1) {
        var row2 = el('div', 'smc-smart__row'); row2.appendChild(el('span', 'smc-smart__label', 'Brand'));
        row2.appendChild(chip('All', !state.brand, function () { state.brand = ''; state.shown = 24; render(); }));
        brands.forEach(function (t) { row2.appendChild(chip(brandLabel(t.name) + ' (' + t.n + ')', state.brand === t.name, function () { state.brand = state.brand === t.name ? '' : t.name; state.shown = 24; render(); })); });
        bar.appendChild(row2);
      }
      var row3 = el('div', 'smc-smart__row smc-smart__row--ctrl');
      row3.appendChild(chip('In stock only', state.stock, function () { state.stock = !state.stock; state.shown = 24; render(); }));
      var sel = el('select', 'smc-smart__sort'); sel.setAttribute('aria-label', 'Sort by');
      [['best', 'Best match'], ['low', 'Price: low to high'], ['high', 'Price: high to low'], ['az', 'Name: A to Z']].forEach(function (o) { var op = el('option', null, o[1]); op.value = o[0]; if (state.sort === o[0]) op.selected = true; sel.appendChild(op); });
      sel.addEventListener('change', function () { state.sort = sel.value; state.shown = 24; render(); });
      row3.appendChild(sel); bar.appendChild(row3);
    }
    function card(it) {
      var art = el('article', 'smc-card smc-pcard');
      var media = el('a', 'smc-pcard__media'); media.href = '/products/' + it.h; media.tabIndex = -1; media.setAttribute('aria-hidden', 'true');
      if (it.i) { var im = el('img'); im.loading = 'lazy'; im.decoding = 'async'; var base = '/cdn/shop/' + it.i + (it.i.indexOf('?') > -1 ? '&' : '?');
        im.src = base + 'width=360'; im.srcset = base + 'width=240 240w, ' + base + 'width=360 360w, ' + base + 'width=480 480w'; im.sizes = '(min-width: 1000px) 280px, 46vw'; im.alt = it.t; media.appendChild(im); }
      if (!it.a) media.appendChild(el('span', 'smc-badge smc-badge--out', 'Sold out')); else if (it.c > it.p) media.appendChild(el('span', 'smc-badge', 'Sale'));
      art.appendChild(media);
      var body = el('div', 'smc-pcard__body'), h = el('h3', 'smc-pcard__title'), a = el('a', null, it.t); a.href = '/products/' + it.h; h.appendChild(a); body.appendChild(h);
      var sz = sizeOf(it.t); if (sz) body.appendChild(el('p', 'smc-pcard__size', sz));
      var price = el('p', 'smc-pcard__price'); if (it.n > 1) price.appendChild(el('span', 'smc-pcard__from', 'From ')); price.appendChild(document.createTextNode(fmt.format(it.p / 100)));
      if (it.c > it.p) price.appendChild(el('s', null, fmt.format(it.c / 100))); body.appendChild(price);
      if (!it.a) { var so = el('a', 'smc-btn smc-btn--outline smc-btn--block smc-btn--muted', 'Sold out'); so.href = '/products/' + it.h; body.appendChild(so); }
      else if (it.n === 1) {
        var f = el('form', 'smc-pcard__form'); f.action = '/cart/add'; f.method = 'post'; f.setAttribute('data-smc-add', '');
        var inp = el('input'); inp.type = 'hidden'; inp.name = 'id'; inp.value = it.d; f.appendChild(inp);
        var bt = el('button', 'smc-btn smc-btn--outline smc-btn--block', 'Add to cart'); bt.type = 'submit'; f.appendChild(bt); body.appendChild(f);
      } else { var co = el('a', 'smc-btn smc-btn--outline smc-btn--block', 'Choose options'); co.href = '/products/' + it.h; body.appendChild(co); }
      art.appendChild(body); var li = el('li'); li.appendChild(art); return li;
    }
    function render() {
      renderBar();
      var list = apply();
      head.textContent = '';
      var h1 = $('.smc-h1'); if (h1) h1.textContent = list.length + (list.length === 1 ? ' result' : ' results') + ' for “' + q + '”';
      if (res.fixed) { var n = el('p', 'smc-pagehead__text'); n.appendChild(document.createTextNode('Showing results for ')); n.appendChild(el('strong', null, res.shown)); head.appendChild(n); }
      grid.textContent = '';
      list.slice(0, state.shown).forEach(function (r) { grid.appendChild(card(r.d.it)); });
      if (!list.length) {
        var e = el('div', 'smc-empty-state'); e.appendChild(el('h2', 'smc-h3', 'Nothing matches those filters'));
        var p = el('p'); p.appendChild(document.createTextNode('Clear a filter, or call ' + (cfg.phone || 'us') + ' and we will find it.')); e.appendChild(p); grid.parentNode.insertBefore(e, grid); e.setAttribute('data-smc-empty', '');
      } else { var old = $('[data-smc-empty]', box); if (old) old.remove(); }
      more.hidden = list.length <= state.shown;
      more.textContent = 'Show more (' + (list.length - state.shown) + ' left)';
    }
    more.addEventListener('click', function () { state.shown += 24; render(); });
    if (toggle) toggle.addEventListener('click', function () { var useNative = !native.hidden; native.hidden = useNative; box.hidden = !useNative; toggle.textContent = useNative ? 'Use store filters instead' : 'Use smart results'; });

    loadIndex(cfg).then(function (eng) {
      if (!eng) return;
      res = eng.search(q);
      if (!res.list.length) return; // leave Shopify's own results (and its empty state) in place
      render();
      box.hidden = false; native.hidden = true;
      if (toggle) { toggle.hidden = false; toggle.textContent = 'Use store filters instead'; }
    });
  }

  function boot() {
    $$('[data-smc-search]').forEach(initSearch);
    $$('[data-smc-smart]').forEach(initResultsPage);
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot); else boot();
})();
